
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/base/SEOHead';

export default function NetworkingPage() {
  const upcomingEvents = [
    {
      title: 'El Paso Startup Mixer',
      description: 'Regular networking events that bring together entrepreneurs and startup founders in a casual environment. These mixers provide opportunities to share ideas, find potential partners, and build meaningful connections within the El Paso startup ecosystem.',
      category: 'Networking',
      frequency: 'Monthly',
    },
    {
      title: 'Small Business Workshop Series',
      description: 'Ongoing educational workshops led by local experts and successful entrepreneurs. These sessions cover essential business skills including financial management, marketing strategies, legal compliance, and operational best practices tailored for El Paso businesses.',
      category: 'Workshop',
      frequency: 'Bi-weekly',
    },
    {
      title: 'Women in Business Luncheon',
      description: 'A supportive community event designed to empower female entrepreneurs through networking and mentorship. These luncheons feature inspiring speakers, panel discussions, and opportunities to connect with other women business leaders in El Paso.',
      category: 'Networking',
      frequency: 'Quarterly',
    },
    {
      title: 'Tech & Innovation Summit',
      description: 'An annual conference exploring the latest technology trends and innovation opportunities in the El Paso region. Attendees gain insights into emerging technologies, digital transformation, and how to leverage innovation for business growth.',
      category: 'Conference',
      frequency: 'Annual',
    },
    {
      title: 'Business After Hours',
      description: 'A rotating networking event that takes place at different El Paso businesses each month. These informal gatherings allow entrepreneurs to explore various business locations while building relationships with other local business owners.',
      category: 'Networking',
      frequency: 'Monthly',
    },
    {
      title: 'Investor Pitch Night',
      description: 'Regular events where entrepreneurs can present their business ideas to local investors and angel groups. These pitch nights provide valuable feedback, potential funding opportunities, and practice in articulating your business vision.',
      category: 'Pitch Event',
      frequency: 'Quarterly',
    },
  ];

  const chambers = [
    {
      name: 'El Paso Chamber of Commerce',
      description: 'The largest and oldest business advocacy organization in the El Paso region, representing thousands of businesses across all industries. They offer networking events, legislative advocacy, workforce development programs, and business resources to help companies grow and succeed in the Borderplex region.',
      icon: 'ri-building-line',
      website: 'https://www.elpaso.org',
      members: '1,800+',
      founded: '1899',
    },
    {
      name: 'El Paso Hispanic Chamber of Commerce',
      description: 'Dedicated to advancing the economic growth of Hispanic-owned businesses in the El Paso area. They provide bilingual business resources, mentorship programs, procurement opportunities, and advocacy for policies that support the Hispanic business community along the U.S.–Mexico border.',
      icon: 'ri-team-line',
      website: 'https://www.ephcc.org',
      members: '1,200+',
      founded: '1974',
    },
    {
      name: 'Greater El Paso Chamber of Commerce (Black Chamber)',
      description: 'Focused on empowering Black-owned businesses and entrepreneurs throughout the El Paso metropolitan area. They provide networking opportunities, business development workshops, access to capital resources, and community partnerships to foster economic equity and growth.',
      icon: 'ri-hand-heart-line',
      website: 'https://www.elpasoblackchamber.org',
      members: '300+',
      founded: '2005',
    },
    {
      name: 'Fort Bliss/El Paso Military Affairs Committee',
      description: 'A specialized chamber initiative that strengthens the relationship between the military community and local businesses. They connect veteran-owned businesses with resources, facilitate defense contracting opportunities, and support military families transitioning into the civilian workforce.',
      icon: 'ri-shield-star-line',
      website: 'https://www.elpaso.org',
      members: '500+',
      founded: '1960',
    },
  ];

  const associations = [
    {
      name: 'SCORE El Paso',
      description: 'A nonprofit association of volunteer business mentors offering free, confidential advice to aspiring and established small business owners. SCORE mentors bring decades of real-world experience across industries and provide one-on-one coaching, workshops, and online resources.',
      icon: 'ri-user-star-line',
      website: 'https://www.score.org/elpaso',
      focus: 'Mentorship & Coaching',
    },
    {
      name: 'Borderplex Alliance',
      description: 'A regional economic development organization serving the El Paso–Las Cruces–Ciudad Juárez metropolitan area. They attract new businesses, support existing companies, and promote the region as a premier destination for investment, talent, and innovation across the international border.',
      icon: 'ri-global-line',
      website: 'https://www.borderplexalliance.org',
      focus: 'Economic Development',
    },
    {
      name: 'El Paso Young Professionals',
      description: 'A dynamic network for emerging business leaders and young entrepreneurs in the El Paso area. They host social events, professional development seminars, and community service projects designed to cultivate the next generation of business and civic leaders in the region.',
      icon: 'ri-rocket-line',
      website: 'https://www.elpasoyp.com',
      focus: 'Young Professionals',
    },
    {
      name: 'National Association of Women Business Owners (NAWBO) – El Paso',
      description: 'The local chapter of the nation\'s leading advocacy organization for women entrepreneurs. NAWBO El Paso provides leadership training, networking events, legislative advocacy, and peer support to help women-owned businesses thrive in the competitive marketplace.',
      icon: 'ri-women-line',
      website: 'https://www.nawbo.org',
      focus: 'Women Entrepreneurs',
    },
    {
      name: 'El Paso Association of Builders',
      description: 'A professional trade association representing home builders, remodelers, and associated trades in the El Paso area. They provide industry education, government relations, networking events, and resources to support the residential and commercial construction industry.',
      icon: 'ri-hammer-line',
      website: 'https://www.elpasobuilders.com',
      focus: 'Construction & Trades',
    },
    {
      name: 'El Paso Association of REALTORS®',
      description: 'The leading professional association for real estate professionals in the El Paso market. They offer continuing education, market data and analytics, advocacy for property rights, and networking opportunities for agents, brokers, and affiliated service providers.',
      icon: 'ri-home-4-line',
      website: 'https://www.epar.org',
      focus: 'Real Estate',
    },
  ];

  const tradeSchools = [
    {
      name: 'El Paso Community College (EPCC) – Workforce Training',
      description: 'EPCC offers a wide range of workforce and continuing education programs designed to prepare students for in-demand careers. Their technical programs include welding, HVAC, automotive technology, healthcare certifications, IT networking, and advanced manufacturing. With multiple campuses across El Paso, EPCC is one of the most accessible and affordable pathways to skilled trades careers in the region.',
      icon: 'ri-tools-line',
      website: 'https://www.epcc.edu',
      image: 'https://public.readdy.ai/ai/img_res/141c26a2d8d66bb8ee18c6f7ea6914bc.jpg',
      programs: ['Welding Technology', 'HVAC & Refrigeration', 'Automotive Technology', 'Healthcare Certifications', 'IT & Networking', 'Advanced Manufacturing'],
    },
    {
      name: 'Western Technical College',
      description: 'A locally established career-focused institution offering hands-on training in high-demand technical fields. Western Tech provides diploma and associate degree programs in diesel mechanics, automotive technology, HVAC, refrigeration, welding, and commercial driving. Their programs emphasize real-world skills with industry-standard equipment, and they maintain strong relationships with local employers for job placement assistance.',
      icon: 'ri-settings-3-line',
      website: 'https://www.westerntech.edu',
      image: 'https://readdy.ai/api/search-image?query=technical%20college%20workshop%20with%20students%20learning%20hands%20on%20mechanical%20skills%20automotive%20repair%20welding%20equipment%20bright%20industrial%20training%20facility&width=600&height=340&seq=trade2&orientation=landscape',
      programs: ['Diesel Mechanics', 'Automotive Technology', 'HVAC & Refrigeration', 'Welding', 'Commercial Driving (CDL)', 'Collision Repair'],
    },
    {
      name: 'Vista College – El Paso Campus',
      description: 'Vista College provides career-oriented training programs in healthcare, skilled trades, business, and technology. Their El Paso campus offers accelerated programs that help students enter the workforce quickly with practical, job-ready skills. Programs include medical assisting, HVAC technology, electrical technology, and business administration, with flexible scheduling options for working adults.',
      icon: 'ri-graduation-cap-line',
      website: 'https://www.vistacollege.edu',
      image: 'https://readdy.ai/api/search-image?query=modern%20vocational%20school%20classroom%20with%20students%20studying%20healthcare%20and%20technology%20programs%20bright%20clean%20educational%20environment%20professional%20training&width=600&height=340&seq=trade3&orientation=landscape',
      programs: ['Medical Assisting', 'HVAC Technology', 'Electrical Technology', 'Business Administration', 'IT Support Specialist', 'Dental Assisting'],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="El Paso Business Networking Events - Chambers, Associations & Trade Schools"
        description="Connect with El Paso's business community. Find networking events, chambers of commerce, professional associations, and trade schools. Build relationships to grow your Texas business."
        canonical="/networking"
        keywords="El Paso networking events, business networking El Paso, El Paso Chamber of Commerce, business associations Texas, trade schools El Paso, professional networking"
      />
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <header className="text-center mb-16">
            <h1 className="text-6xl font-bold text-gray-900 mb-6">Networking Events</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Connect with El Paso's vibrant business community. Attend events, join organizations, and build relationships that will help your business thrive.
            </p>
          </header>

          {/* Regular Events & Programs */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-gray-900 mb-10">Regular Events &amp; Programs</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {upcomingEvents.map((event, index) => (
                <article
                  key={index}
                  className="bg-[#FAFAF8] rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="bg-gradient-to-br from-[#D2691E] to-[#B8571A] p-6">
                    <span className="inline-block bg-white/20 text-white text-xs font-semibold px-3 py-1 rounded-full mb-3">
                      {event.category}
                    </span>
                    <h3 className="text-2xl font-bold text-white mb-2">{event.title}</h3>
                    <div className="flex items-center space-x-2 text-white/90 text-sm">
                      <i className="ri-calendar-line"></i>
                      <span>{event.frequency}</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-sm text-gray-700 leading-relaxed">{event.description}</p>
                  </div>
                </article>
              ))}
            </div>
          </section>

          {/* Business Organizations */}
          <section className="mb-20">
            <div className="text-center mb-14">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Business Organizations</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                El Paso is home to a strong network of chambers, professional associations, and trade schools that support business growth and workforce development.
              </p>
            </div>

            {/* Chambers of Commerce */}
            <article className="mb-16">
              <div className="flex items-center space-x-3 mb-8">
                <div className="w-10 h-10 bg-[#1B3A3A] rounded-lg flex items-center justify-center">
                  <i className="ri-government-line text-xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Chambers of Commerce</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {chambers.map((chamber, index) => (
                  <div
                    key={index}
                    className="bg-[#FAFAF8] rounded-2xl p-7 hover:shadow-xl transition-all duration-300 border border-gray-100 group"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="w-14 h-14 bg-[#1B3A3A] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                        <i className={`${chamber.icon} text-2xl text-white`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-lg font-bold text-gray-900 mb-2 leading-snug">{chamber.name}</h4>
                        <div className="flex items-center space-x-4 mb-3">
                          <span className="text-xs font-medium text-[#D2691E] bg-[#D2691E]/10 px-2.5 py-1 rounded-full whitespace-nowrap">
                            {chamber.members} Members
                          </span>
                          <span className="text-xs text-gray-500 whitespace-nowrap">Est. {chamber.founded}</span>
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed mb-4">{chamber.description}</p>
                        <a
                          href={chamber.website}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          className="inline-flex items-center space-x-2 text-[#D2691E] font-semibold text-sm hover:text-[#B8571A] transition-colors cursor-pointer"
                          aria-label={`Visit ${chamber.name} website`}
                        >
                          <span>Visit Website</span>
                          <i className="ri-external-link-line"></i>
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </article>

            {/* Professional Associations */}
            <article className="mb-16">
              <div className="flex items-center space-x-3 mb-8">
                <div className="w-10 h-10 bg-[#D2691E] rounded-lg flex items-center justify-center">
                  <i className="ri-links-line text-xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Professional Associations</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {associations.map((assoc, index) => (
                  <div
                    key={index}
                    className="bg-[#FAFAF8] rounded-2xl p-6 hover:shadow-xl transition-all duration-300 border border-gray-100 group"
                  >
                    <div className="w-12 h-12 bg-[#D2691E]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#D2691E]/20 transition-colors">
                      <i className={`${assoc.icon} text-2xl text-[#D2691E]`}></i>
                    </div>
                    <span className="inline-block text-xs font-medium text-[#1B3A3A] bg-[#1B3A3A]/10 px-2.5 py-1 rounded-full mb-3 whitespace-nowrap">
                      {assoc.focus}
                    </span>
                    <h4 className="text-lg font-bold text-gray-900 mb-2 leading-snug">{assoc.name}</h4>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4">{assoc.description}</p>
                    <a
                      href={assoc.website}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="inline-flex items-center space-x-2 text-[#D2691E] font-semibold text-sm hover:text-[#B8571A] transition-colors cursor-pointer"
                      aria-label={`Visit ${assoc.name} website`}
                    >
                      <span>Visit Website</span>
                      <i className="ri-external-link-line"></i>
                    </a>
                  </div>
                ))}
              </div>
            </article>

            {/* Local Trade Schools */}
            <article>
              <div className="flex items-center space-x-3 mb-8">
                <div className="w-10 h-10 bg-[#2C5F4F] rounded-lg flex items-center justify-center">
                  <i className="ri-graduation-cap-line text-xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Local Trade Schools</h3>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {tradeSchools.map((school, index) => (
                  <div
                    key={index}
                    className="bg-[#FAFAF8] rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-100 group"
                  >
                    <div className="w-full h-48 overflow-hidden">
                      <img
                        src={school.image}
                        alt={`${school.name} campus and training facilities`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <div className="w-11 h-11 bg-[#2C5F4F] rounded-xl flex items-center justify-center -mt-12 mb-4 relative z-10 shadow-lg">
                        <i className={`${school.icon} text-xl text-white`}></i>
                      </div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2 leading-snug">{school.name}</h4>
                      <p className="text-sm text-gray-600 leading-relaxed mb-4">{school.description}</p>
                      <div className="mb-5">
                        <p className="text-xs font-semibold text-gray-800 uppercase tracking-wide mb-2">Popular Programs</p>
                        <div className="flex flex-wrap gap-1.5">
                          {school.programs.map((program, pIndex) => (
                            <span
                              key={pIndex}
                              className="text-xs bg-[#2C5F4F]/10 text-[#2C5F4F] px-2.5 py-1 rounded-full font-medium"
                            >
                              {program}
                            </span>
                          ))}
                        </div>
                      </div>
                      <a
                        href={school.website}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="inline-flex items-center space-x-2 text-[#2C5F4F] font-semibold text-sm hover:text-[#1B3A3A] transition-colors cursor-pointer"
                        aria-label={`Visit ${school.name} website`}
                      >
                        <span>Visit Website</span>
                        <i className="ri-external-link-line"></i>
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </article>
          </section>

          <aside className="mt-20 bg-gradient-to-r from-[#1B3A3A] to-[#2C5F4F] rounded-3xl p-12 text-center">
            <h2 className="text-4xl font-bold text-white mb-4">Host Your Own Event?</h2>
            <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              We'd love to feature your networking event or workshop on our platform. Get in touch to share your event details.
            </p>
            <button className="inline-flex items-center space-x-3 bg-[#D2691E] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#B8571A] transition-all shadow-lg cursor-pointer whitespace-nowrap">
              <span>Submit Your Event</span>
              <i className="ri-arrow-right-line text-xl"></i>
            </button>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}
